import type { ReactNode } from "react";
import { Form } from "ra-core";
import { CalendarEventDeleteButton } from "@/modules/calendar/CalendarEventDeleteButton";
import { DateInput } from "@/components/admin/date-input";
import { SaveButton } from "@/components/admin/form";
import { TextInput } from "@/components/admin/text-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { SelectInput } from "@/components/admin/select-input";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { CalendarTimeInput } from "@/modules/calendar/CalendarTimeInput";
import {
  DURATION_CHOICES,
  DURATION_NONE,
} from "@/modules/calendar/calendarReminderOptions";
import { MeetingContactTitleSync } from "@/modules/meetings/meetingFormUtils";
import { MeetingFormatFields } from "@/modules/meetings/MeetingFormatFields";
import { MeetingShareOptions } from "@/modules/meetings/MeetingShareOptions";
import { CalendarReminderOffsetsInput } from "@/modules/calendar/CalendarReminderOffsetsInput";
import { TeamMemberMultiSelect } from "@/modules/shared/TeamMemberMultiSelect";
import { WorkCreateAccountLinkFields } from "@/modules/work/WorkCreateAccountLinkFields";

const formatDuration = (value?: number | null) =>
  value == null ? DURATION_NONE : value;

const parseDuration = (value: string | number) => {
  if (value === DURATION_NONE || value === "" || value == null) return null;
  return Number(value);
};

/** Label above the field (the old side column left inputs ~300px wide). */
const MeetingField = ({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn("min-w-0 space-y-1.5", className)}>
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
    {children}
  </div>
);

export const MeetingScheduleFormFields = ({
  isEdit,
  embedded = false,
  onDeleteSuccess,
  onDeleteError,
  emailConfigured = false,
  shareEmail = false,
  shareSms = false,
  onShareEmailChange,
  onShareSmsChange,
  showShareOptions = false,
}: {
  isEdit: boolean;
  embedded?: boolean;
  onDeleteSuccess?: () => void;
  onDeleteError?: (error: unknown) => void;
  emailConfigured?: boolean;
  shareEmail?: boolean;
  shareSms?: boolean;
  onShareEmailChange?: (value: boolean) => void;
  onShareSmsChange?: (value: boolean) => void;
  showShareOptions?: boolean;
}) => {
  return (
    <>
      <MeetingContactTitleSync />

      {!embedded ? (
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit meeting" : "Schedule meeting"}
          </DialogTitle>
        </DialogHeader>
      ) : null}

      <div className="@container space-y-5">
        {/* Who */}
        <div className="grid gap-4 @md:grid-cols-2">
          <WorkCreateAccountLinkFields
            requireContact
            showCreateContact
            title={null}
            renderRow={(label, children) => (
              <MeetingField label={label}>{children}</MeetingField>
            )}
          />
        </div>

        <MeetingField label="Title">
          <TextInput
            source="title"
            label={false}
            helperText={false}
            validate={(value) => (value?.trim() ? undefined : "Required")}
          />
        </MeetingField>

        {/* When: one row */}
        <div className="grid gap-4 @md:grid-cols-3">
          <MeetingField label="Date">
            <DateInput
              source="event_date"
              label={false}
              validate={(value) => (value ? undefined : "Required")}
            />
          </MeetingField>
          <MeetingField label="Start time">
            <CalendarTimeInput
              source="event_time"
              label={false}
              helperText={false}
            />
          </MeetingField>
          <MeetingField label="Duration">
            <SelectInput
              source="duration_minutes"
              label={false}
              choices={[...DURATION_CHOICES]}
              format={formatDuration}
              parse={parseDuration}
              helperText={false}
            />
          </MeetingField>
        </div>

        <MeetingField label="Where">
          <MeetingFormatFields />
        </MeetingField>

        <MeetingField label="Team">
          <TeamMemberMultiSelect
            source="assignee_member_ids"
            label={false}
            required
            placeholder="Select meeting assignees"
          />
        </MeetingField>

        <MeetingField label="Reminders">
          <CalendarReminderOffsetsInput label={false} showLabel={false} />
        </MeetingField>

        <MeetingField label="Notes">
          <TextInput
            source="description"
            label={false}
            multiline
            helperText={false}
          />
        </MeetingField>
      </div>

      {showShareOptions && onShareEmailChange && onShareSmsChange ? (
        <MeetingShareOptions
          emailConfigured={emailConfigured}
          shareEmail={shareEmail}
          shareSms={shareSms}
          onShareEmailChange={onShareEmailChange}
          onShareSmsChange={onShareSmsChange}
        />
      ) : null}

      {isEdit && !embedded ? (
        <BooleanInput
          source="completed_at"
          label="Mark as done"
          format={(value) => Boolean(value)}
          parse={(value) => (value ? new Date().toISOString() : null)}
          helperText={false}
        />
      ) : null}

      {!embedded ? (
        // Stays in view while the form scrolls.
        <DialogFooter className="sticky -bottom-6 z-10 -mx-6 -mb-6 w-auto gap-4 border-t bg-background px-6 py-4 sm:justify-between">
          {isEdit ? (
            <CalendarEventDeleteButton
              onSuccess={onDeleteSuccess}
              onError={onDeleteError}
              label="Delete meeting"
            />
          ) : (
            <span />
          )}
          <SaveButton
            type="button"
            label={isEdit ? "Save meeting" : "Schedule meeting"}
          />
        </DialogFooter>
      ) : null}
    </>
  );
};

export type MeetingScheduleFormFieldsProps = {
  isEdit: boolean;
  embedded?: boolean;
  onDeleteSuccess?: () => void;
  onDeleteError?: (error: unknown) => void;
  emailConfigured?: boolean;
  shareEmail?: boolean;
  shareSms?: boolean;
  onShareEmailChange?: (value: boolean) => void;
  onShareSmsChange?: (value: boolean) => void;
  showShareOptions?: boolean;
};

export const MeetingScheduleForm = ({
  isEdit,
  onDeleteSuccess,
  onDeleteError,
  emailConfigured = false,
  shareEmail = false,
  shareSms = false,
  onShareEmailChange,
  onShareSmsChange,
  showShareOptions = false,
}: {
  isEdit: boolean;
  onDeleteSuccess?: () => void;
  onDeleteError?: (error: unknown) => void;
  emailConfigured?: boolean;
  shareEmail?: boolean;
  shareSms?: boolean;
  onShareEmailChange?: (value: boolean) => void;
  onShareSmsChange?: (value: boolean) => void;
  showShareOptions?: boolean;
}) => (
  <Form className="flex flex-col gap-4">
    <MeetingScheduleFormFields
      isEdit={isEdit}
      onDeleteSuccess={onDeleteSuccess}
      onDeleteError={onDeleteError}
      emailConfigured={emailConfigured}
      shareEmail={shareEmail}
      shareSms={shareSms}
      onShareEmailChange={onShareEmailChange}
      onShareSmsChange={onShareSmsChange}
      showShareOptions={showShareOptions}
    />
  </Form>
);
